import { createApiClient, createQueriesConfig, QueryConfigTypes } from '@wix/premium-fed-sdk/@apis-infra-react';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { ExperimentSpecs } from '../constants';
export class WhoisNetworkError extends Error {
  constructor(message) {
    super(message);
    this.name = 'WhoisNetworkError';
  }
}

/** Carries an HTTP status for lookup outcomes — a `404` signals the domain is
 *  available (registry RDAP / WHOIS "no match"), which the page uses to redirect
 *  to search. Used instead of attaching a status to a bare Error. */
export class WhoisHttpError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
    this.name = 'WhoisHttpError';
  }
}
const domainNotFound = () => new WhoisHttpError('Domain not found', 404);

/** When on, the card renders from RDAP without blocking on the slow port-43
 *  WHOIS call; raw WHOIS text loads independently via the `whoisRaw` query. */
function isProgressiveLookupOn() {
  var _getPremiumEssentials;
  return !!((_getPremiumEssentials = getPremiumEssentialsSafe()) != null && (_getPremiumEssentials = _getPremiumEssentials.experiments) != null && _getPremiumEssentials[ExperimentSpecs.WhoisProgressiveLookup]);
}

/** HTTP status off a lookup error, whatever its shape (`404` = available). */
export function httpStatusOf(err) {
  var _e$response;
  if (err instanceof WhoisHttpError) {
    return err.status;
  }
  // Status carried by the api-client / HttpError-shaped errors (getRdapUrl,
  // whoisLookup, parseRdap) — read defensively since the shape varies.
  if (err == null || typeof err !== 'object') {
    return undefined;
  }
  const e = err;
  const status = e.status ?? e.httpStatus ?? ((_e$response = e.response) == null ? void 0 : _e$response.status);
  return typeof status === 'number' ? status : undefined;
}
function getRelatedLink(rdapData) {
  const links = rdapData.links;
  const related = links == null ? void 0 : links.find(l => l.rel === 'related');
  return related == null ? void 0 : related.href;
}
async function fetchRdapFromBrowser(url) {
  let response;
  try {
    response = await fetch(url, {
      headers: {
        Accept: 'application/rdap+json, application/json'
      }
    });
  } catch (err) {
    if (err instanceof TypeError) {
      throw err;
    }
    throw new Error(`RDAP fetch failed: ${String(err)}`);
  }
  if (!response.ok) {
    // Carry the status so callers can distinguish "domain available" (404)
    // from other RDAP failures without re-querying WHOIS.
    throw new WhoisHttpError(`RDAP fetch failed: ${response.status}`, response.status);
  }
  return response.json();
}

/** Fetch RDAP JSON, falling back to the server-side proxy on CORS errors (TypeError).
 *  HTTP errors (4xx/5xx) are re-thrown directly so callers can distinguish
 *  "domain not found" (404) from a network/CORS failure. */
async function fetchRdap(url, proxyRdap) {
  try {
    return await fetchRdapFromBrowser(url);
  } catch (err) {
    // Only proxy on CORS/network errors (TypeError). HTTP errors mean the
    // RDAP server responded — proxying would get the same result, so just
    // re-throw so the caller can fall back to the WHOIS path.
    if (!(err instanceof TypeError)) {
      throw err;
    }
    const proxyResult = await proxyRdap({
      url
    });
    return proxyResult.data ?? {};
  }
}
async function fetchRdapPath(rdapUrl, domain, rawWhois, parseRdap, proxyRdap) {
  const registryData = await fetchRdap(`${rdapUrl}domain/${domain}`, proxyRdap);
  const registryParsed = await parseRdap({
    registryData
  });
  const relatedUrl = getRelatedLink(registryData);
  if (relatedUrl) {
    try {
      const registrarData = await fetchRdap(relatedUrl, proxyRdap);
      const fullParsed = await parseRdap({
        registryData,
        registrarData
      });
      return {
        ...fullParsed.info,
        raw: rawWhois
      };
    } catch {
      // The registrar referral RDAP can fail (404/down/CORS) for a domain that
      // is registered — the registry fetch above already returned 200, proving
      // the domain exists. Fall back to the registry-only parse rather than let
      // a registrar 404 bubble up and be misread as "domain available".
    }
  }
  return {
    ...registryParsed.info,
    raw: rawWhois
  };
}

/**
 * Common WHOIS "no match" phrases that indicate the domain is available.
 * The `m` flag makes `^` match the start of any line, not just the start of
 * the whole response — many registries (e.g. PIR for .org) prepend a
 * terms-of-use header before the actual not-found message.
 */
const NO_MATCH_PATTERNS = [/^No match for\b/im,
// VeriSign (.com, .net)
/^NOT FOUND\b/im,
// PIR (.org), many others
/^Domain not found\b/im,
// PIR (.org) — appears after boilerplate header
/^No entries found\b/im, /^Object does not exist\b/im, /^No Data Found\b/im];
export function isNotFoundResponse(raw) {
  const text = (raw.registry ?? raw.registrar ?? '').trimStart();
  return text.length > 0 && NO_MATCH_PATTERNS.some(p => p.test(text));
}
function bareWhoisFallback(domain, rawWhois) {
  return {
    id: domain,
    domainInfo: {
      name: domain,
      nameservers: [],
      status: []
    },
    registrant: {
      redacted: true
    },
    registrar: {},
    tos: {},
    raw: rawWhois,
    source: 'WHOIS'
  };
}

/**
 * Legacy orchestration (progressive lookup spec OFF):
 * 1. Fire in parallel: getRdapUrl + whoisLookup(parse=true) — blocks on both
 * 2. RDAP path: browser fetch → CORS proxy fallback → parse
 * 3. If RDAP fails, fall back to WHOIS parsed data
 * 4. If WHOIS raw text is a "no match" response, throw 404 → redirect to search
 */
const performLegacyLookup = async _ref => {
  var _rdapUrlResult$value, _whoisData$info;
  let httpClient = _ref.httpClient,
    domain = _ref.request.domain;
  const queries = DomainsApiClient.queries({
    httpClient
  });
  const _await$Promise$allSet = await Promise.allSettled([queries.getRdapUrl({
      domain
    }), queries.whoisLookup({
      domain,
      parse: true
    })]),
    rdapUrlResult = _await$Promise$allSet[0],
    whoisResult = _await$Promise$allSet[1];
  const rdapUrl = rdapUrlResult.status === 'fulfilled' ? (_rdapUrlResult$value = rdapUrlResult.value) == null ? void 0 : _rdapUrlResult$value.url : null;
  const whoisData = whoisResult.status === 'fulfilled' ? whoisResult.value : null;
  const rawWhois = (whoisData == null || (_whoisData$info = whoisData.info) == null ? void 0 : _whoisData$info.raw) ?? {};
  if (rdapUrl) {
    try {
      return await fetchRdapPath(rdapUrl, domain, rawWhois, queries.parseRdap, queries.proxyRdap);
    } catch {
      // RDAP failed — fall through to WHOIS path
    }
  }
  if (isNotFoundResponse(rawWhois)) {
    throw domainNotFound();
  }
  if (whoisData != null && whoisData.info) {
    return {
      ...whoisData.info
    };
  }
  if (whoisResult.status === 'fulfilled') {
    return bareWhoisFallback(domain, rawWhois);
  }

  // Re-throw the original WHOIS error only when it carries a 404 status
  // (domain is available); all other failures are a generic network error.
  if (httpStatusOf(whoisResult.reason) === 404) {
    throw whoisResult.reason;
  }
  throw new WhoisNetworkError('WHOIS lookup failed');
};

/**
 * Progressive orchestration (spec ON): RDAP-first, never blocks the card on the
 * slow port-43 WHOIS call. Raw WHOIS text loads separately via `whoisRaw`.
 * 1. getRdapUrl (cacheable, fast)
 * 2. RDAP path drives the card; an available domain returns HTTP 404
 * 3. ccTLDs (no RDAP) or RDAP failures fall back to whoisLookup(parse=true)
 */
const performProgressiveCardLookup = async _ref2 => {
  var _await$queries$getRda;
  let httpClient = _ref2.httpClient,
    domain = _ref2.request.domain;
  const queries = DomainsApiClient.queries({
    httpClient
  });
  const rdapUrl = (_await$queries$getRda = await queries.getRdapUrl({
    domain
  }).catch(() => null)) == null ? void 0 : _await$queries$getRda.url;
  if (rdapUrl) {
    try {
      // raw is left empty here — the `whoisRaw` query fills it in independently.
      return await fetchRdapPath(rdapUrl, domain, {}, queries.parseRdap, queries.proxyRdap);
    } catch (err) {
      if (httpStatusOf(err) === 404) {
        throw domainNotFound();
      }
      // Other RDAP failures (CORS, 5xx, parse) fall through to WHOIS.
    }
  }

  // ccTLD (no RDAP) or RDAP failure: WHOIS drives the card.
  try {
    var _whoisData$info2;
    const whoisData = await queries.whoisLookup({
      domain,
      parse: true
    });
    const rawWhois = (whoisData == null || (_whoisData$info2 = whoisData.info) == null ? void 0 : _whoisData$info2.raw) ?? {};
    if (isNotFoundResponse(rawWhois)) {
      throw domainNotFound();
    }
    if (whoisData != null && whoisData.info) {
      return {
        ...whoisData.info
      };
    }
    return bareWhoisFallback(domain, rawWhois);
  } catch (err) {
    if (httpStatusOf(err) === 404) {
      throw err;
    }
    throw new WhoisNetworkError('WHOIS lookup failed');
  }
};
const performLookup = args => isProgressiveLookupOn() ? performProgressiveCardLookup(args) : performLegacyLookup(args);

/** Raw WHOIS text only (parse=false). Loaded independently of the card so the
 *  port-43 call never blocks the RDAP-driven card. Used only when the
 *  progressive lookup spec is on. */
const performRawLookup = async _ref3 => {
  var _res$info;
  let httpClient = _ref3.httpClient,
    domain = _ref3.request.domain;
  const queries = DomainsApiClient.queries({
    httpClient
  });
  const res = await queries.whoisLookup({
    domain,
    parse: false
  });
  const raw = (res == null || (_res$info = res.info) == null ? void 0 : _res$info.raw) ?? {};
  return {
    registry: raw.registry,
    registrar: raw.registrar
  };
};
const whoisApiConfig = createQueriesConfig({
  whoisLookup: {
    queryFn: performLookup,
    queryConfigType: QueryConfigTypes.REALTIME_NO_REFETCH
  },
  whoisRaw: {
    queryFn: performRawLookup,
    queryConfigType: QueryConfigTypes.REALTIME_NO_REFETCH
  }
});
export const WhoisApiClient = createApiClient(whoisApiConfig);
//# sourceMappingURL=whois.apiClient.js.map